import React, {useState, useEffect} from 'react';
import api from '../utils/api';
import {toast} from 'react-toastify';
import Layout from '../components/Layout';

const Publications = () => {
    const [publications, setPublications] = useState([]);
    const [filteredPublications, setFilteredPublications] = useState([]);
    const [magazines, setMagazines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const [showReloadModal, setShowReloadModal] = useState(false);
    const [selectedPublication, setSelectedPublication] = useState(null);
    const [scheduleIssues, setScheduleIssues] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterMagazine, setFilterMagazine] = useState('');
    const [filterIssue, setFilterIssue] = useState('');

    const [formData, setFormData] = useState({
        title: '',
        magazineId: '',
        issueDate: '',
        selectedIssue: '',
        pdfFile: null,
        isPublished: true
    });

    useEffect(() => {
        fetchPublications();
        fetchMagazines();
    }, []);

    useEffect(() => {
        let filtered = [...publications];

        if (searchTerm) {
            filtered = filtered.filter(pub => pub.title.toLowerCase().includes(searchTerm.toLowerCase()) || (pub.issue && pub.issue.toLowerCase().includes(searchTerm.toLowerCase())));
        }

        if (filterMagazine) {
            filtered = filtered.filter(pub => pub.magazineId?._id === filterMagazine);
        }

        if (filterIssue) {
            filtered = filtered.filter(pub => pub.issue === filterIssue);
        }

        setFilteredPublications(filtered);
    }, [publications, searchTerm, filterMagazine, filterIssue]);

    const fetchPublications = async () => {
        try {
            const response = await api.get('/publications');
            setPublications(response.data);
        } catch (error) {
            toast.error('Failed to fetch publications');
            console.error('Error fetching publications:', error);
        } finally {
            setLoading(false);
        }
    };

    const getUniqueIssues = () => {
        const issues = publications.filter(pub => pub.issue).map(pub => pub.issue);
        return [...new Set(issues)];
    };

    const fetchMagazines = async () => {
        try {
            const response = await api.get('/magazines');
            setMagazines(response.data);
        } catch (error) {
            console.error('Failed to fetch magazines:', error);
        }
    };

    const handleInputChange = async e => {
        const {name, value, type, checked} = e.target;

        if (name === 'magazineId' && value) {
            try {
                const response = await api.get(`/magazines/${value}`);
                const magazine = response.data;

                console.log('Magazine data:', magazine);

                if (magazine.schedule) {
                    const scheduleId = typeof magazine.schedule === 'object' ? magazine.schedule._id : magazine.schedule;
                    const scheduleResponse = await api.get(`/schedules/${scheduleId}`);
                    console.log('Schedule issues:', scheduleResponse.data.issues);
                    setScheduleIssues(scheduleResponse.data.issues || []);
                } else {
                    console.log('No schedule found for magazine');
                    setScheduleIssues([]);
                }

                setFormData(prev => ({
                    ...prev,
                    magazineId: value,
                    selectedIssue: '',
                    issueDate: ''
                }));
            } catch (error) {
                console.error('Failed to fetch schedule:', error);
                setScheduleIssues([]);
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        } else if (name === 'selectedIssue' && value) {
            const selectedIssue = scheduleIssues.find(issue => issue._id === value);
            if (selectedIssue) {
                setFormData(prev => ({
                    ...prev,
                    selectedIssue: value,
                    issueDate: selectedIssue.closeDate
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleFileChange = e => {
        setFormData(prev => ({
            ...prev,
            pdfFile: e.target.files[0]
        }));
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!formData.pdfFile) {
            toast.error('Please select a PDF file');
            return;
        }

        const selectedIssueData = scheduleIssues.find(issue => issue._id === formData.selectedIssue);

        const data = new FormData();
        data.append('pdf', formData.pdfFile);
        data.append('title', formData.title);
        data.append('magazineId', formData.magazineId);
        data.append('issueDate', formData.issueDate);
        data.append('issue', selectedIssueData?.name || '');
        data.append('isPublished', formData.isPublished);

        setUploading(true);
        try {
            const response = await api.post('/publications', data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            toast.success('Publication uploaded successfully');
            setShowUploadModal(false);
            setFormData({
                title: '',
                magazineId: '',
                issueDate: '',
                selectedIssue: '',
                pdfFile: null,
                isPublished: true
            });
            setScheduleIssues([]);

            setSelectedPublication(response.data);
            setShowEmbedModal(true);

            fetchPublications();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to upload publication');
        } finally {
            setUploading(false);
        }
    };

    const handleReload = async e => {
        e.preventDefault();

        if (!formData.pdfFile) {
            toast.error('Please select a PDF file');
            return;
        }

        const data = new FormData();
        data.append('pdf', formData.pdfFile);

        setUploading(true);
        try {
            await api.post(`/publications/${
                selectedPublication._id
            }/reload`, data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            toast.success('Publication reloaded successfully');
            setShowReloadModal(false);
            setSelectedPublication(null);
            setFormData({
                title: '',
                magazineId: '',
                issueDate: '',
                selectedIssue: '',
                pdfFile: null,
                isPublished: true
            });
            fetchPublications();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to reload publication');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async id => {
        if (!window.confirm('Are you sure you want to delete this publication?')) {
            return;
        }

        try {
            await api.delete(`/publications/${id}`);
            toast.success('Publication deleted successfully');
            fetchPublications();
        } catch (error) {
            toast.error('Failed to delete publication');
        }
    };

    const togglePublish = async publication => {
        try {
            await api.put(`/publications/${
                publication._id
            }`, {
                isPublished: !publication.isPublished
            });
            toast.success(`Publication ${
                !publication.isPublished ? 'published' : 'unpublished'
            }`);
            fetchPublications();
        } catch (error) {
            toast.error('Failed to update publication');
        }
    };

    const formatUploadDate = date => {
        if (!date) 
            return 'N/A';
        


        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
        ];
        const month = months[d.getMonth()];
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    if (loading) {
        return <div className="p-6">Loading...</div>;
    }

    return (
        <Layout>
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">E-Publications</h1>
                    <button onClick={
                            () => setShowUploadModal(true)
                        }
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                        Upload Publication
                    </button>
                </div>

                {/* Search and Filter */}
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Search</label>
                            <input type="text" placeholder="Search by title or issue..."
                                value={searchTerm}
                                onChange={
                                    e => setSearchTerm(e.target.value)
                                }
                                className="w-full px-3 py-2 border rounded"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Filter by Publication</label>
                            <select value={filterMagazine}
                                onChange={
                                    e => setFilterMagazine(e.target.value)
                                }
                                className="w-full px-3 py-2 border rounded">
                                <option value="">All Publications</option>
                                {
                                magazines.map(mag => (
                                    <option key={
                                            mag._id
                                        }
                                        value={
                                            mag._id
                                    }>
                                        {
                                        mag.name
                                    } </option>
                                ))
                            } </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Filter by Issue</label>
                            <select value={filterIssue}
                                onChange={
                                    e => setFilterIssue(e.target.value)
                                }
                                className="w-full px-3 py-2 border rounded">
                                <option value="">All Issues</option>
                                {
                                getUniqueIssues().map(issue => (
                                    <option key={issue}
                                        value={issue}>
                                        {issue} </option>
                                ))
                            } </select>
                        </div>
                    </div>
                </div>

                {/* Publications Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {
                    filteredPublications.map(pub => (
                        <div key={
                                pub._id
                            }
                            className="bg-white rounded-lg shadow p-4">
                            <h3 className="font-bold text-lg mb-2">
                                {
                                pub.title
                            }</h3>
                            <p className="text-sm text-gray-600 mb-1">
                                Publication: {
                                pub.magazineId?.name || 'N/A'
                            } </p>
                            {
                            pub.issue && <p className="text-sm text-gray-600 mb-1">Issue: {
                                pub.issue
                            }</p>
                        }
                            <p className="text-sm text-gray-600 mb-1">
                                Upload Date: {
                                formatUploadDate(pub.pdfFile?.uploadDate)
                            } </p>
                            {
                            pub.totalPages && (
                                <p className="text-sm text-gray-600 mb-1">Pages: {
                                    pub.totalPages
                                }</p>
                            )
                        }
                            <p className="text-sm text-gray-600 mb-1">Views: {
                                pub.views
                            }</p>
                            <p className="text-sm mb-3">
                                Status:{' '}
                                <span className={
                                    pub.isPublished ? 'text-green-600' : 'text-gray-600'
                                }>
                                    {
                                    pub.isPublished ? 'Published' : 'Draft'
                                } </span>
                            </p>

                            <div className="flex flex-col gap-2">
                                <div className="flex gap-2">
                                    <button onClick={
                                            () => window.open(`/publications/${
                                                pub._id
                                            }`, '_blank')
                                        }
                                        className="flex-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium">
                                        View
                                    </button>
                                    <button onClick={
                                            () => togglePublish(pub)
                                        }
                                        className="flex-1 px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium">
                                        {
                                        pub.isPublished ? 'Unpublish' : 'Publish'
                                    } </button>
                                    <button onClick={
                                            () => handleDelete(pub._id)
                                        }
                                        className="flex-1 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium">
                                        Delete
                                    </button>
                                </div>
                                <div className="flex gap-2">
                                    {
                                    pub.isPublished && (
                                        <button onClick={
                                                () => {
                                                    setSelectedPublication(pub);
                                                    setShowEmbedModal(true);
                                                }
                                            }
                                            className="flex-1 px-3 py-1.5 bg-gray-600 text-white rounded hover:bg-gray-700 text-xs font-medium">
                                            Get Embed Code
                                        </button>
                                    )
                                }
                                    <button onClick={
                                            () => {
                                                setSelectedPublication(pub);
                                                setShowReloadModal(true);
                                            }
                                        }
                                        className="flex-1 px-3 py-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 text-xs font-medium">
                                        Reload
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                } </div>

                {
                filteredPublications.length === 0 && publications.length > 0 && (
                    <div className="text-center text-gray-600 mt-8">
                        No publications match your search criteria.
                    </div>
                )
            }

                {
                publications.length === 0 && (
                    <div className="text-center text-gray-600 mt-8">
                        No publications yet. Upload your first publication to get started.
                    </div>
                )
            }

                {/* Embed Modal */}
                {
                showEmbedModal && selectedPublication && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                            <h2 className="text-xl font-bold mb-4 flex items-center">
                                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                                </svg>
                                Embed on Website
                            </h2>

                            <p className="text-sm text-gray-600 mb-4">
                                Share this publication with anyone. No login required.
                            </p>

                            {/* Public Link */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium mb-2">Public Link (No Login Required)</label>
                                <p className="text-xs text-gray-500 mb-2">
                                    This link can be shared with customers via email or embedded on your website.
                                </p>
                                <div className="flex gap-2">
                                    <input type="text" readOnly
                                        value={
                                            `${
                                                window.location.origin
                                            }/view/publication/${
                                                selectedPublication._id
                                            }`
                                        }
                                        className="flex-1 px-3 py-2 border rounded bg-gray-50 text-sm"/>
                                    <button onClick={
                                            () => {
                                                navigator.clipboard.writeText(`${
                                                    window.location.origin
                                                }/view/publication/${
                                                    selectedPublication._id
                                                }`);
                                                toast.success('Public link copied!');
                                            }
                                        }
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                                        Copy
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <button onClick={
                                        () => {
                                            setShowEmbedModal(false);
                                            setSelectedPublication(null);
                                        }
                                    }
                                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

                {/* Upload Modal */}
                {
                showUploadModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                            <h2 className="text-xl font-bold mb-4">Upload Publication</h2>
                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Publication Name</label>
                                    <input type="text" name="title"
                                        value={
                                            formData.title
                                        }
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border rounded"
                                        placeholder="E.g. Mytown November"
                                        required/>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Publication</label>
                                    <select name="magazineId"
                                        value={
                                            formData.magazineId
                                        }
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border rounded"
                                        required>
                                        <option value="">Select Publication</option>
                                        {
                                        magazines.map(mag => (
                                            <option key={
                                                    mag._id
                                                }
                                                value={
                                                    mag._id
                                            }>
                                                {
                                                mag.name
                                            } </option>
                                        ))
                                    } </select>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Issue</label>
                                    <select name="selectedIssue"
                                        value={
                                            formData.selectedIssue
                                        }
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border rounded"
                                        required
                                        disabled={
                                            !formData.magazineId
                                    }>
                                        <option value="">Select Issue</option>
                                        {
                                        scheduleIssues.length > 0 ? scheduleIssues.map(issue => (
                                            <option key={
                                                    issue._id
                                                }
                                                value={
                                                    issue._id
                                            }>
                                                {
                                                issue.name
                                            } </option>
                                        )) : formData.magazineId && <option disabled>No issues available</option>
                                    } </select>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">PDF File</label>
                                    <input type="file" accept=".pdf"
                                        onChange={handleFileChange}
                                        className="w-full px-3 py-2 border rounded"
                                        required/>
                                </div>

                                <div className="flex gap-2">
                                    <button type="submit"
                                        disabled={uploading}
                                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center">
                                        {
                                        uploading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Uploading...
                                            </>
                                        ) : ('Upload')
                                    } </button>
                                    <button type="button"
                                        onClick={
                                            () => setShowUploadModal(false)
                                        }
                                        disabled={uploading}
                                        className="flex-1 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

                {/* Reload Modal */}
                {
                showReloadModal && selectedPublication && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                            <h2 className="text-xl font-bold mb-4">Reload Publication</h2>
                            <p className="text-sm text-gray-600 mb-4">
                                Replace the PDF file for:
                                <strong>{
                                    selectedPublication.title
                                }</strong>
                            </p>
                            <form onSubmit={handleReload}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">New PDF File</label>
                                    <input type="file" accept=".pdf"
                                        onChange={handleFileChange}
                                        className="w-full px-3 py-2 border rounded"
                                        required/>
                                </div>

                                <div className="flex gap-2">
                                    <button type="submit"
                                        disabled={uploading}
                                        className="flex-1 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center justify-center">
                                        {
                                        uploading ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Reloading...
                                            </>
                                        ) : ('Reload')
                                    } </button>
                                    <button type="button"
                                        onClick={
                                            () => {
                                                setShowReloadModal(false);
                                                setSelectedPublication(null);
                                            }
                                        }
                                        disabled={uploading}
                                        className="flex-1 px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:cursor-not-allowed">
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            } </div>
        </Layout>
    );
};

export default Publications;
